import React from 'react';
import { Users, Calendar, Shield, FileCheck, TrendingUp, Award } from 'lucide-react';

const GobiernoCorporativo = () => {
  const consejoAdministracion = [
    {
      nombre: 'Ricardo Dehesa Cortés',
      cargo: 'Presidente del Consejo y Notario Titular',
      descripcion: 'Líder estratégico y autoridad notarial máxima'
    },
    {
      nombre: 'Daniel Sanchez Perez',
      cargo: 'Director de Tecnología e Innovación',
      descripcion: 'Responsable del ecosistema tecnológico y transformación digital'
    },
    // Placeholders para otros miembros
    {
      nombre: 'Miembro del Consejo',
      cargo: 'Director Jurídico',
      descripcion: 'Supervisión de cumplimiento normativo y legal'
    },
    {
      nombre: 'Miembro del Consejo',
      cargo: 'Director Financiero',
      descripcion: 'Gestión financiera y estrategia de crecimiento'
    }
  ];

  const gobernanzaTrimestral = [
    {
      trimestre: 'Q1',
      enfoque: 'Revisión de Protocolos',
      actividades: [
        'Auditoría de procesos notariales',
        'Actualización de sistemas de ciberseguridad',
        'Revisión de cumplimiento normativo'
      ]
    },
    {
      trimestre: 'Q2',
      enfoque: 'Innovación Tecnológica',
      actividades: [
        'Evaluación de nuevas herramientas digitales',
        'Capacitación del equipo en Legaltech',
        'Desarrollo de integraciones blockchain'
      ]
    },
    {
      trimestre: 'Q3',
      enfoque: 'Expansión y Alianzas',
      actividades: [
        'Revisión de alianzas estratégicas (Arkangeles, Hitchain)',
        'Análisis de nuevas oportunidades de mercado',
        'Evaluación de proyectos de tokenización'
      ]
    },
    {
      trimestre: 'Q4',
      enfoque: 'Evaluación Anual',
      actividades: [
        'Balance general del año',
        'Planificación estratégica del próximo año',
        'Aprobación de presupuestos e inversiones'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-notaria-primary to-notaria-secondary py-20 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Users className="w-20 h-20 mx-auto mb-6" />
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Gobierno Corporativo
            </h1>
            <p className="text-2xl text-cyan-100">
              Transparencia, Ética y Liderazgo Institucional en la Notaría 4.0
            </p>
          </div>
        </div>
      </section>

      {/* Introducción */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-notaria-primary mb-6">
              Nuestro Compromiso con la Excelencia
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              La Notaría Pública 90 opera bajo un esquema de Gobierno Corporativo moderno y profesional. 
              No somos una oficina unipersonal, sino una <strong>institución con visión estratégica</strong>, 
              donde un Consejo de Administración supervisa la dirección tecnológica, jurídica y financiera, 
              garantizando que la innovación nunca comprometa la ética legal.
            </p>
          </div>
        </div>
      </section>

      {/* Consejo de Administración */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <Shield className="w-16 h-16 text-notaria-primary mx-auto mb-4" />
              <h2 className="text-4xl font-bold text-notaria-primary mb-4">
                Consejo de Administración
              </h2>
              <p className="text-lg text-gray-600">
                Órgano colegiado que supervisa la dirección estratégica y tecnológica
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {consejoAdministracion.map((miembro, index) => (
                <div 
                  key={index}
                  className="bg-gradient-to-br from-gray-50 to-gray-100 p-6 rounded-lg shadow-md hover:shadow-xl transition-shadow border-l-4 border-notaria-secondary"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 bg-notaria-primary rounded-full flex items-center justify-center flex-shrink-0">
                      <Users className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-notaria-primary mb-1">
                        {miembro.nombre}
                      </h3>
                      <p className="text-notaria-secondary font-semibold mb-2">
                        {miembro.cargo}
                      </p>
                      <p className="text-gray-600 text-sm">
                        {miembro.descripcion}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Esquema de Gobernanza Trimestral */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <Calendar className="w-16 h-16 text-notaria-secondary mx-auto mb-4" />
              <h2 className="text-4xl font-bold text-notaria-primary mb-4">
                Esquema de Gobernanza Trimestral
              </h2>
              <p className="text-lg text-gray-600">
                Sesiones periódicas para auditoría, revisión y mejora continua
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {gobernanzaTrimestral.map((periodo, index) => (
                <div 
                  key={index}
                  className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-all hover:-translate-y-1"
                >
                  <div className="text-center mb-4">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-notaria-primary text-white rounded-full text-2xl font-bold mb-3">
                      {periodo.trimestre}
                    </div>
                    <h3 className="text-xl font-bold text-notaria-primary mb-2">
                      {periodo.enfoque}
                    </h3>
                  </div>
                  <ul className="space-y-2">
                    {periodo.actividades.map((actividad, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                        <FileCheck className="w-4 h-4 text-notaria-secondary mt-0.5 flex-shrink-0" />
                        <span>{actividad}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Principios de Gobierno */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold text-notaria-primary mb-12 text-center">
              Principios Fundamentales
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Transparencia */}
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-cyan-100 rounded-full mb-4">
                  <Shield className="w-10 h-10 text-cyan-600" />
                </div>
                <h3 className="text-2xl font-bold text-notaria-primary mb-3">
                  Transparencia
                </h3>
                <p className="text-gray-700">
                  Comunicación abierta y clara sobre nuestras operaciones, procesos y decisiones estratégicas.
                </p>
              </div>

              {/* Rendición de Cuentas */}
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full mb-4">
                  <TrendingUp className="w-10 h-10 text-blue-600" />
                </div>
                <h3 className="text-2xl font-bold text-notaria-primary mb-3">
                  Rendición de Cuentas
                </h3>
                <p className="text-gray-700">
                  Auditorías trimestrales y revisión constante de procesos para garantizar el cumplimiento normativo.
                </p>
              </div>

              {/* Ética e Integridad */}
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-purple-100 rounded-full mb-4">
                  <Award className="w-10 h-10 text-purple-600" />
                </div>
                <h3 className="text-2xl font-bold text-notaria-primary mb-3">
                  Ética e Integridad
                </h3>
                <p className="text-gray-700">
                  Compromiso inquebrantable con los más altos estándares éticos y profesionales en cada acto notarial.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-notaria-primary to-notaria-secondary text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">
              ¿Quieres conocer más sobre nuestra estructura de gobernanza?
            </h2>
            <p className="text-xl text-gray-200 mb-8">
              Contacta con nuestro equipo para obtener información detallada sobre nuestro Consejo de Administración y políticas de gobierno corporativo.
            </p>
            <a 
              href="/contacto"
              className="inline-block bg-white text-notaria-primary font-bold px-8 py-4 rounded-lg hover:bg-gray-100 transition-colors"
            >
              Contactar
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GobiernoCorporativo;
